"use client";

import { Core } from "@faire/design-tokens";
import { isNotUndefined } from "@faire/web--source/common/typescriptUtils";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { useUniqueId } from "@faire/web--source/ui/hooks/useUniqueId";
import * as React from "react";
import styled, { useTheme } from "styled-components";

import { Spacer } from "../../spacing";
import { Typography } from "../../Typography";
import { getDerivedPropsFromErrorProps, IErrorProps } from "../error";
import { FormInputLabel, inputStyles } from "../index";

const InnerTextArea = styled.textarea<{
  $showErrorState: boolean;
  $resizable?: boolean;
}>`
  ${inputStyles};

  appearance: none;
  resize: ${(props) => (props.$resizable ? "vertical" : "none")};
  padding: 8px 16px;
  color: ${(props) =>
    props.disabled ? Core.text.disabled : Core.text.default};
`;

const CharacterCount = styled(Typography).attrs({
  variant: "labelSansRegular",
})`
  &&& {
    color: ${(props) => props.theme?.input.helper?.color ?? Core.text.subdued};
    text-align: end;
    position: relative;
    right: 4px;
  }
`;

const ErrorText = styled(Typography).attrs({
  variant: "labelSansRegular",
  color: Core.text.critical,
})`
  flex: 1;
`;

const HelperText = styled(Typography).attrs({ variant: "labelSansRegular" })`
  &&& {
    color: ${(props) => props.theme?.input.helper?.color ?? Core.text.subdued};
    flex: 1;
  }
`;

export interface IProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    IErrorProps {
  helper?: string | React.ReactNode;
  label?: string;
  resizable?: boolean;
}

/**
 * @deprecated Use `@faire/slate/core/text-area` instead - since 2025-04-04
 *
 * @trackcomponent
 *
 * @link [Docs](https://slate.faire.team/slate/component/input/web/text-area-input)
 */
export const TextArea: React.FC<
  IProps & React.RefAttributes<HTMLTextAreaElement>
> = (props) => {
  const value: string = (props.value ?? "") as string;
  const { error, helper, id, name, label, maxLength, resizable, ...rest } =
    props;
  const theme = useTheme();
  const uniqueId = useUniqueId("text-area-input-");
  const { showErrorState, showErrorMessage, errorMessage } =
    getDerivedPropsFromErrorProps({ error });

  let message = null;
  if (showErrorMessage) {
    message = <ErrorText>{errorMessage}</ErrorText>;
  } else if (helper) {
    message = <HelperText>{helper}</HelperText>;
  }

  return (
    <Column style={{ flex: 1, height: "inherit" }}>
      <Column style={{ height: "inherit" }}>
        {label ? (
          <FormInputLabel
            $typography={{
              ...(theme.input.label ?? {}),
              color: showErrorState
                ? Core.text.critical
                : (theme.input.label?.color ?? Core.text.default),
            }}
            htmlFor={id ?? uniqueId}
            style={{ height: "inherit" }}
          >
            {label}
          </FormInputLabel>
        ) : null}
        <InnerTextArea
          $resizable={resizable}
          $showErrorState={showErrorState}
          id={id ?? uniqueId}
          name={name}
          maxLength={maxLength}
          {...rest}
        />
      </Column>
      {maxLength || errorMessage || helper ? <Spacer size={4} /> : null}
      <Row justify="space-between" style={{ flexDirection: "row-reverse" }}>
        {isNotUndefined(maxLength) ? (
          <>
            <CharacterCount theme={theme}>
              {value.length}/{maxLength}
            </CharacterCount>
            <Spacer size={10} />
          </>
        ) : null}
        {message}
      </Row>
    </Column>
  );
};
